import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';

import Input from '@rover/components/src/formFields/Input';
import Icon from '@rover/components/src/typography/Icon';
import { EmailVerificationEvents } from '@rover/constants/src/sitter/emailVerification.constants';
import { Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import ImageHeader from '@rover/kibble/patterns/ImageHeader';
import Section from '@rover/kibble/patterns/Section';
import useTheme from '@rover/kibble/themes/useTheme';
import { VerticalLayout } from '@rover/react-lib/src/components/layouts';
import Form from '@rover/react-lib/src/components/utils/Form';
import type { EmailVerification } from '@rover/types/src/EmailVerification';
import { EmailVerificationStatus } from '@rover/types/src/EmailVerification';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import inject from '@rover/utilities/injector';
import { redirect } from '@rover/utilities/url';

type Props = {
  headerText: string;
  imageHeaderContent: string;
  formLabel: string;
  placeholderText: string;
  image: string;
  verifyButtonText: string;
  verifyingButtonText: string;
  resendButtonText: string;
  skipButtonText: string;
  verifiedText: string;
  verifyStatus: number | null;
  isSubmitting: boolean;
  resendCode: (refresh?: boolean) => void;
  verifyEmail: (arg0: EmailVerification) => void;
  errorMessage: string;
  isBlockingModal: boolean;
  emailVerificationStatus: EmailVerificationStatus;
  logoutCurrentUser: (arg0: { nextLocation: string; ref?: string }) => void;
};

/**
 * Helper to handle logout - sends postMessage to React Native if in WebView,
 * and always calls the normal logout flow to trigger navigation.
 *
 * The navigation away from verification automatically closes the modal in React Native,
 * while the postMessage ensures the native app state is updated.
 */
const handleLogoutClick = (
  nextUrl: string,
  logoutCurrentUser: (arg0: { nextLocation: string }) => void
): void => {
  // Check if running in React Native WebView
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, rover/no-platform-specific-globals-or-imports
  if ((window as any).ReactNativeWebView) {
    // Send logout message to React Native to update app state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, rover/no-platform-specific-globals-or-imports
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'logout_requested',
        source: 'email_verification',
      })
    );
  }

  // Always call the normal logout flow - this triggers navigation away from verification,
  // which automatically closes the verification modal in the CIAF app.
  logoutCurrentUser({ nextLocation: nextUrl });
};

const VerifyEmail = (props: Props): JSX.Element => {
  const theme = useTheme();
  const {
    headerText,
    imageHeaderContent,
    formLabel,
    placeholderText,
    image,
    verifyButtonText,
    verifyingButtonText,
    resendButtonText,
    skipButtonText,
    verifiedText,
    verifyStatus,
    isSubmitting,
    resendCode,
    verifyEmail,
    emailVerificationStatus,
    errorMessage,
    isBlockingModal,
    logoutCurrentUser,
  } = props;
  const previousUrl = inject('Rover.context.previousUrl', '');
  const nextUrl = inject('Rover.context.nextUrl', '');

  const [isVerifying, setIsVerifying] = useState(false);
  const [textInputClicked, setTextInputClicked] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isVerificationSuccessful, setIsVerificationSuccessful] = useState(false);

  const currentCode = useRef('');

  const RESEND_TIME = 10;
  const intervalCountdown: { current: ReturnType<typeof setInterval> | undefined } =
    useRef(undefined);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendText, setResendText] = useState(resendButtonText);
  const [showAlphanumError, setShowAlphanumError] = useState(false);
  const alphanumericalError = t`This code is incorrect. The code is case sensitive and must only contain alphanumeric characters (A–Z and 0–9). Check your email and try again.`;
  const logoutText = t`Log out`;

  const decreaseResendCountdown = (): void => {
    setResendCountdown((prev) => {
      if (prev <= 1 && intervalCountdown.current) {
        clearInterval(intervalCountdown.current);
      }
      return prev - 1;
    });
  };

  const startResendCountdown = (): void => {
    setResendCountdown(RESEND_TIME);
    intervalCountdown.current = setInterval(decreaseResendCountdown, 1000);
  };

  const triggerGenericEvents = (
    eventName: string,
    error?: string | null,
    code?: string,
    submitVerifyStatus?: boolean
  ): void => {
    const submitStatus =
      submitVerifyStatus !== undefined
        ? { submitStatus: submitVerifyStatus ? 'success' : 'fail' }
        : {};
    fireDataLayerEvent({
      event: eventName,
      flowSource: previousUrl,
      flow: emailVerificationStatus,
      ...(error && error.length > 0 ? { errorMessage: error } : {}),
      ...(code ? { code } : {}),
      ...submitStatus,
      observability_tags: {
        flow: emailVerificationStatus,
        ...submitStatus,
      },
    });
  };

  const triggerModalAknowledge = (): void => {
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    isBlockingModal && $(document).trigger('blocking-modal-acknowledged');
  };

  useEffect(() => {
    resendCode();
    startResendCountdown();
    triggerGenericEvents(EmailVerificationEvents.VERIFY_EMAIL_LOAD);
    return () => {
      if (intervalCountdown.current) {
        clearInterval(intervalCountdown.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentCode.current === '') {
      return;
    }
    setIsVerificationSuccessful(verifyStatus === 200);
    triggerGenericEvents(
      EmailVerificationEvents.VERIFY_EMAIL_VERIFY_BUTTON_CLICK,
      errorMessage,
      currentCode.current,
      verifyStatus === 200
    );
  }, [verifyStatus]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isVerificationSuccessful) {
      timer = setTimeout(() => {
        if (isBlockingModal) triggerModalAknowledge();
        else redirect(previousUrl);
      }, 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerificationSuccessful]);

  useEffect(() => {
    if (!isSubmitting) {
      setIsVerifying(false);
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (resendCountdown > 0) {
      setResendText(t`Send new code in ${resendCountdown} seconds`);
    } else {
      setResendText(resendButtonText);
    }
  }, [resendCountdown, resendButtonText]);

  const handleSubmit = (code: string, e?: SyntheticEvent<HTMLButtonElement>): void => {
    if (e && e.preventDefault) e.preventDefault();
    setIsVerifying(true);
    currentCode.current = code;
    verifyEmail({ code: code.trim(), emailVerificationStatus });
  };

  const onResendCode = (onChange): void => {
    onChange({ code: '' });
    setIsDirty(false);
    resendCode(true);
    startResendCountdown();
    triggerGenericEvents(EmailVerificationEvents.VERIFY_EMAIL_RESEND_CODE_BUTTON_CLICK);
  };

  const onSkip = (): void => {
    triggerGenericEvents(EmailVerificationEvents.VERIFY_EMAIL_SKIP_BUTTON_CLICK);
    !isBlockingModal && redirect(previousUrl);
  };

  const isAlphanumeric = (code: string | undefined): void => {
    if (code && !!/[^a-zA-Z0-9]/i.test(code)) {
      setShowAlphanumError(true);
      return;
    }
    setShowAlphanumError(false);
  };

  const shouldShowAlphanumError = (): string | undefined => {
    if (showAlphanumError) {
      return alphanumericalError;
    }
    return undefined;
  };

  const shouldShowSkipButton = (): boolean => {
    if (emailVerificationStatus === EmailVerificationStatus.COMPULSORY || isBlockingModal)
      return false;
    return true;
  };

  const shouldShowLogoutButton = (): boolean => {
    if (emailVerificationStatus === EmailVerificationStatus.COMPULSORY || isBlockingModal)
      return true;
    return false;
  };

  return (
    <Section maxWidth={560} my={isBlockingModal ? '0x' : '16x'}>
      <ImageHeader
        heading={headerText}
        headingSize="400"
        image={image}
        description={t`Paper airplane doodle`}
        resizeMethod="contain"
      >
        <Paragraph>{imageHeaderContent}</Paragraph>
      </ImageHeader>
      <Form initialData={{ code: '' }} onSubmit={({ code }) => code && handleSubmit(code)}>
        {(form) => (
          <>
            <Input
              name="code"
              label={formLabel}
              placeholder={placeholderText}
              disabled={isSubmitting}
              validationError={errorMessage || shouldShowAlphanumError()}
              validationType="inline"
              onChange={(code) => {
                setIsDirty(true);
                isAlphanumeric(code);
                form.onChange({
                  code,
                });
                if (!textInputClicked) {
                  setTextInputClicked(true);
                  triggerGenericEvents(EmailVerificationEvents.VERIFY_EMAIL_ADD_CODE);
                }
              }}
              value={form.data.code}
            />
            {isVerificationSuccessful ? (
              <Flex alignItems="center" justifyContent="center">
                <Icon
                  icon="rover-icon-thin-check"
                  style={{
                    fontSize: theme.display[400].fontSize,
                    color: theme.textColor.success,
                    fontWeight: theme.display[400].fontWeight,
                  }}
                />
                <Text pt="1x" fontWeight="semibold" textColor="success">
                  {verifiedText}
                </Text>
              </Flex>
            ) : (
              <VerticalLayout>
                <Button
                  onClick={form.onSubmit}
                  loading={isVerifying}
                  disabled={isSubmitting || !isDirty || showAlphanumError}
                  variant="primary"
                  mt="1x"
                  mb="4x"
                  fullWidth
                  data-qa-id="submit"
                >
                  {isVerifying ? verifyingButtonText : verifyButtonText}
                </Button>
                <Button
                  onClick={() => onResendCode(form.onChange)}
                  loading={resendCountdown > 0}
                  disabled={isSubmitting || resendCountdown > 0}
                  variant="flat"
                  mb="4x"
                  fullWidth
                  data-qa-id="resend"
                  data-testid="email-verification-resend"
                >
                  {resendText}
                </Button>
                {shouldShowSkipButton() && (
                  <Button
                    onClick={onSkip}
                    disabled={isSubmitting}
                    variant="flat"
                    fullWidth
                    data-qa-id="skip"
                  >
                    {skipButtonText}
                  </Button>
                )}
                {shouldShowLogoutButton() && (
                  <Button
                    onClick={() => {
                      handleLogoutClick(nextUrl, logoutCurrentUser);
                      triggerGenericEvents(
                        EmailVerificationEvents.VERIFY_EMAIL_LOGOUT_BUTTON_CLICK
                      );
                    }}
                    disabled={isSubmitting}
                    variant="flat"
                    fullWidth
                    data-qa-id="logout"
                    data-testid="email-verification-logout"
                  >
                    {logoutText}
                  </Button>
                )}
              </VerticalLayout>
            )}
          </>
        )}
      </Form>
    </Section>
  );
};
export default VerifyEmail;
